class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// ─── Predictive Search: Auto-Add + Live Drawer Refresh ──────────────────────
//(function() {
//  if (window.__predictiveCartInitted) return;
//  window.__predictiveCartInitted = true;
//
//  // helper to re-fetch & replace the drawer sections
//  async function refreshCartDrawer() {
//    const cartUrl = window.routes?.cart_url || '/cart';
//    try {
//      const text = await fetch(`${cartUrl}?section_id=cart-drawer`).then(r => r.text());
//      const html = new DOMParser().parseFromString(text, 'text/html');
//
//      // 1) Replace drawer items
//      const newItems  = html.querySelector('cart-drawer-items');
//      const oldItems  = document.querySelector('cart-drawer-items');
//      if (newItems && oldItems) oldItems.replaceWith(newItems);
//
//      // 2) Replace drawer footer
//      const newFooter = html.querySelector('.cart-drawer__footer');
//      const oldFooter = document.querySelector('.cart-drawer__footer');
//      if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
//    } catch (e) {
//      console.error('Error refreshing cart drawer:', e);
//    }
//  }
//
//  document.addEventListener('change', async (event) => {
//    const input = event.target.closest('input.quantity__input');
//    if (!input) return;
//
//    const variantId = input.dataset.quantityVariantId;
//    const quantity  = parseInt(input.value, 10);
//    if (!variantId || isNaN(quantity) || quantity < 1) return;
//
//    try {
//      // ── 1) Add to cart ─────────────────────────────────────────────────────
//      const addRes = await fetch('/cart/add.js', {
//        method: 'POST',
//        headers: { 'Content-Type': 'application/json' },
//        body: JSON.stringify({ id: variantId, quantity })
//      });
//      if (!addRes.ok) {
//        const err = await addRes.json();
//        console.error('Predictive add error:', err);
//        return alert(err.message || 'Unable to add to cart');
//      }
//
//      // ── 2) Open & refresh drawer ───────────────────────────────────────────
//      document.querySelector('cart-drawer')?.open?.();
//      await refreshCartDrawer();
//
//      // ── 3) Update cart-bubble count ──────────────────────────────────────
//      const cart = await fetch('/cart.js').then(r => r.json());
//      const bubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
//      if (bubble) {
//        bubble.textContent = cart.item_count;
//        bubble.classList.toggle('hidden', cart.item_count === 0);
//      }
//    } catch (error) {
//      console.error('Predictive search cart add failed:', error);
//    }
//  });
//})();

// ─── Predictive Search: Cart Update Functionality ──────────────────────
(function () {
  if (window.__predictiveCartInit) return;
  window.__predictiveCartInit = true;

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('input.quantity__input');
    if (!input) return;

    const variantId = input.dataset.quantityVariantId;
    const quantity = parseInt(input.value, 10);
    //if (!variantId || isNaN(quantity) || quantity < 1) return;
    if (!variantId || isNaN(quantity)) return;

    try {
      // First check if the item is already in the cart
      const cart = await fetch('/cart.js').then(r => r.json());
      const existingItem = cart.items.find(item => item.variant_id === parseInt(variantId));
      
      let res;
      if (existingItem) {
        // If item exists, update its quantity
        res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: existingItem.key,
            quantity: quantity
          }),
        });
      //} else {
      } else if (quantity > 0) {
        // If item doesn't exist, add it to cart
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity }),
        });
      } else {
        // If quantity is 0, remove the item
        res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: existingItem.key,
            quantity: 0
          }),
        });
      }

      const payload = await res.json();
      if (!res.ok) {
        console.error('Error updating cart:', payload);
        alert(payload.message || 'Unable to update cart');
        return;
      }

      // Get the cart drawer component
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      // Fetch cart sections
      const sections = [
        { id: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', selector: '.shopify-section' }
      ];

      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}?sections=${sections.map(s => s.id).join(',')}`);
      const sectionsData = await response.json();

      // Update each section
      sections.forEach(({ id, selector }) => {
        const sectionElement = document.getElementById(id);
        if (!sectionElement) return;

        const newContent = new DOMParser()
          .parseFromString(sectionsData[id], 'text/html')
          .querySelector(selector);

        if (newContent) {
          const targetElement = sectionElement.querySelector(selector) || sectionElement;
          targetElement.innerHTML = newContent.innerHTML;
        }
      });

      // Update cart drawer state
      cartDrawer.classList.toggle('is-empty', payload.item_count === 0);
      
      // Handle empty cart state
      const emptyState = cartDrawer.querySelector('.drawer__inner-empty');
      if (emptyState) {
        emptyState.style.display = payload.item_count === 0 ? 'flex' : 'none';
      }

      // Ensure proper structure for cart items
      const cartItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartItems) {
        cartItems.style.display = 'flex';
        cartItems.style.flexDirection = 'column';
        cartItems.style.flex = '1';
        cartItems.style.overflow = 'auto';
      }

      // Update checkout button state
      const checkoutButton = cartDrawer.querySelector('#CartDrawer-Checkout');
      if (checkoutButton) {
        checkoutButton.disabled = payload.item_count === 0;
      }

      // Open the cart drawer
      //cartDrawer.open();

      // Trigger the cart update event with the correct data structure
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'predictive-search',
        cartData: {
          item_count: payload.item_count,
          items: payload.items,
          sections: sectionsData,
          variant_id: variantId
        }
      });

      // Update all quantity inputs for this variant across the site
      const quantityInputs = document.querySelectorAll(`input.quantity__input[data-quantity-variant-id="${variantId}"]`);
      quantityInputs.forEach(input => {
        if (input !== event.target) { // Don't update the input that triggered the change
          let cartItem = null;
          if (Array.isArray(payload.items)) {
            cartItem = payload.items.find(item => item.variant_id === parseInt(variantId));
          }
          if (cartItem) {
            input.value = cartItem.quantity;
          } else {
            input.value = 0;
          }
        }
      });

    } catch (error) {
      console.error('Predictive search cart update failed:', error);
    }
  });
})();

// Subscribe to cart update events from any source
subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  if (event.source === 'predictive-search') return; // Skip if we triggered the update

  const cartData = event.cartData;
  if (!cartData?.items) return;

  // Update all quantity inputs based on cart items
  cartData.items.forEach(item => {
    const quantityInputs = document.querySelectorAll(`input.quantity__input[data-quantity-variant-id="${item.variant_id}"]`);
    quantityInputs.forEach(input => {
      input.value = item.quantity;
    });
  });
});

// Listen for cart drawer quantity changes
document.addEventListener('change', (event) => {
  const input = event.target.closest('input.quantity__input');
  if (!input || !input.closest('cart-drawer-items')) return;

  const variantId = input.dataset.quantityVariantId;
  if (!variantId) return;

  // Update all other quantity inputs for this variant
  const quantityInputs = document.querySelectorAll(`input.quantity__input[data-quantity-variant-id="${variantId}"]`);
  quantityInputs.forEach(otherInput => {
    if (otherInput !== input) {
      otherInput.value = input.value;
    }
  });
});
